import { GEOFENCE_LIMITS, type Geofence, type Site } from "@fts/core";
import { useCallback, useEffect, useState } from "react";
import { Icon } from "../components/Icon";
import { Panel } from "../components/ui";
import { useI18n, type MessageKey } from "../i18n";
import { canEditGeofences, kindRule, shapeSummary, vehicleTypesSummary } from "../lib/geofences";
import { useSession, type TenantData } from "../state/session";
import { DeleteGeofenceDialog, GeofenceFormDialog } from "./GeofenceForm";

type Modal = { kind: "create" } | { kind: "edit"; zone: Geofence } | { kind: "delete"; zone: Geofence } | null;

/**
 * "Bölgeler" of a site (ADR 0015): forbidden zones, work areas and dwell limits defined on THIS site.
 * Everyone who sees the site reads them; safety officers, fleet managers and admins edit them.
 */
export function SiteGeofencesPanel({ data, site }: { data: TenantData; site: Site }) {
  const { t, locale } = useI18n();
  const { provider, tenant, role } = useSession();
  const [zones, setZones] = useState<Geofence[] | null>(null);
  const [error, setError] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const canEdit = canEditGeofences(role);
  const tenantId = tenant?.id;

  const reload = useCallback(async () => {
    if (!tenantId) return;
    try {
      // Only the zones defined on this site (zones of parent sites are listed on their own site page).
      setZones(await provider.listGeofences(tenantId, site.id));
      setError(false);
    } catch {
      setError(true);
      setZones((z) => z ?? []);
    }
  }, [provider, tenantId, site.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const full = (zones?.length ?? 0) >= GEOFENCE_LIMITS.perSite;
  const done = async (message: string) => {
    setModal(null);
    setNotice(message);
    await reload();
  };

  return (
    <Panel
      title={t("geofence.title")}
      meta={
        canEdit && zones !== null && !error ? (
          <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "create" })} disabled={full} title={full ? t("geofence.limitReached", { max: GEOFENCE_LIMITS.perSite }) : undefined}>
            <Icon name="plus" size={15} />
            {t("geofence.add")}
          </button>
        ) : undefined
      }
      flush
    >
      <p className="panel-text zone-intro">{t("geofence.intro")}</p>
      {notice && (
        <p className="success-banner zone-notice" role="status">
          {notice}
        </p>
      )}
      {zones === null ? (
        <div className="loading" role="status">
          <span className="spinner" aria-hidden="true" />
          {t("common.loading")}
        </div>
      ) : error ? (
        <p className="form-error zone-notice" role="alert">
          {t("geofence.loadError")}
        </p>
      ) : zones.length === 0 ? (
        <p className="table-empty">{canEdit ? t("geofence.emptyEditable") : t("geofence.empty")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("geofence.tableCaption", { site: site.name })}</caption>
            <thead>
              <tr>
                <th scope="col">{t("geofence.name")}</th>
                <th scope="col">{t("geofence.kind")}</th>
                <th scope="col" className="col-hide-sm">
                  {t("geofence.shape")}
                </th>
                <th scope="col" className="col-hide-sm">
                  {t("geofence.vehicleTypes")}
                </th>
                <th scope="col">{t("geofence.status")}</th>
                {canEdit && (
                  <th scope="col">
                    <span className="sr-only">{t("geofence.actions")}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => (
                <tr key={z.id}>
                  <td className="strong">{z.name}</td>
                  <td>
                    <span className="zone-kind-cell">
                      <span className={`zone-swatch zone-${z.kind}`} aria-hidden="true" />
                      <span>
                        {t(`geofence.kind.${z.kind}` as MessageKey)}
                        <span className="field-hint">{kindRule(t, z)}</span>
                      </span>
                    </span>
                  </td>
                  <td className="col-hide-sm">{shapeSummary(t, z, locale)}</td>
                  <td className="col-hide-sm">{vehicleTypesSummary(t, z.vehicleTypes)}</td>
                  <td>
                    <span className={`badge ${z.active ? "tone-ok" : "tone-neutral"}`}>{z.active ? t("geofence.active") : t("geofence.inactive")}</span>
                  </td>
                  {canEdit && (
                    <td className="num">
                      <span className="row-actions">
                        <button type="button" className="icon-button" onClick={() => setModal({ kind: "edit", zone: z })} title={t("geofence.editNamed", { name: z.name })}>
                          <Icon name="edit" size={16} label={t("geofence.editNamed", { name: z.name })} />
                        </button>
                        <button type="button" className="icon-button" onClick={() => setModal({ kind: "delete", zone: z })} title={t("geofence.deleteNamed", { name: z.name })}>
                          <Icon name="trash" size={16} label={t("geofence.deleteNamed", { name: z.name })} />
                        </button>
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!canEdit && zones !== null && zones.length > 0 && <p className="panel-footnote">{t("geofence.readOnly")}</p>}

      {(modal?.kind === "create" || modal?.kind === "edit") && (
        <GeofenceFormDialog
          site={site}
          sites={data.sites}
          zone={modal.kind === "edit" ? modal.zone : undefined}
          siteZones={zones ?? []}
          onClose={() => setModal(null)}
          onSaved={(g) => void done(t("geofence.saved", { name: g.name }))}
        />
      )}
      {modal?.kind === "delete" && <DeleteGeofenceDialog zone={modal.zone} onClose={() => setModal(null)} onDeleted={() => void done(t("geofence.deleted", { name: modal.zone.name }))} />}
    </Panel>
  );
}
